import React from 'react';
import './About.css';

const About = () => {
  return (
    <div className="about-container">
      <div className="about-header">
        <h1>About JAM</h1>
        <p className="subtitle">Your comprehensive solution for managing job applications</p>
      </div>

      <div className="about-content">
        <section className="intro-section">
          <h2>What is JAM?</h2>
          <p>
            Job Application Manager is a powerful web application designed to help job seekers 
            efficiently manage and track their job applications throughout the entire hiring process. 
            Whether you're applying to multiple companies or just starting your job search, 
            our platform provides the tools you need to stay organized and never miss an opportunity.
          </p>
        </section>

        <section className="features-section">
          <h2>Key Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📧</div>
              <h3>Gmail Integration</h3>
              <p>
                Automatically scan your Gmail for job application emails and classify them 
                using advanced machine learning. No more manual tracking of application confirmations, 
                interview invitations, or rejection emails.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h3>AI-Powered Classification</h3>
              <p>
                Our intelligent system automatically categorizes emails into relevant statuses: 
                Submitted, Interview, Assessment, Offer, or Rejected. Save time and never miss 
                important updates.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Interactive Dashboard</h3>
              <p>
                Get a comprehensive overview of your job search progress with our visual dashboard. 
                Filter by status, track application counts, and drill down into specific categories 
                for detailed insights.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">✏️</div>
              <h3>Manual Entry</h3>
              <p>
                Add applications manually with our intuitive form. Capture all important details 
                including company, position, salary, location, and personal notes for each application.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🔐</div>
              <h3>Secure Authentication</h3>
              <p>
                Sign in securely with Google OAuth2. Your data is protected and only accessible 
                to you. No passwords to remember, just use your Google account.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>Mobile Responsive</h3>
              <p>
                Access your job applications from anywhere with our fully responsive design. 
                Works perfectly on desktop, tablet, and mobile devices.
              </p>
            </div>
          </div>
        </section>

        <section className="how-it-works-section">
          <h2>How It Works</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Sign In with Google</h3>
                <p>
                  Securely authenticate using your Google account. No need to create another password 
                  or remember login credentials.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Authorize Gmail Access</h3>
                <p>
                  Grant permission to scan your Gmail for job-related emails. We only access 
                  emails and never store your email content permanently.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Automatic Processing</h3>
                <p>
                  Our AI system scans your recent emails, identifies job applications, and 
                  automatically extracts company names, positions, and application status.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Track & Manage</h3>
                <p>
                  View all your applications in the dashboard, filter by status, add manual 
                  entries, and keep detailed notes for each application.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="technology-section">
          <h2>Technology Stack</h2>
          <div className="tech-grid">
            <div className="tech-category">
              <h3>Frontend</h3>
              <ul>
                <li>React.js - Modern UI framework</li>
                <li>React Router - Navigation</li>
                <li>CSS3 - Styling and animations</li>
                <li>Google OAuth2 - Authentication</li>
              </ul>
            </div>

            <div className="tech-category">
              <h3>Backend</h3>
              <ul>
                <li>Django - Python web framework</li>
                <li>Django REST Framework - API</li>
                <li>PostgreSQL/SQLite - Database</li>
                <li>JWT - Token authentication</li>
              </ul>
            </div>

            <div className="tech-category">
              <h3>AI & ML</h3>
              <ul>
                <li>OpenAI Embeddings - Text analysis</li>
                <li>Scikit-learn - Machine learning</li>
                <li>Logistic Regression - Classification</li>
                <li>Natural Language Processing</li>
              </ul>
            </div>

            <div className="tech-category">
              <h3>APIs & Services</h3>
              <ul>
                <li>Gmail API - Email access</li>
                <li>Google OAuth2 - Authentication</li>
                <li>OpenAI API - AI processing</li>
                <li>RESTful APIs - Data exchange</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Privacy & Security</h2>
          <div className="privacy-content">
            <div className="privacy-point">
              <h3>🔒 Data Protection</h3>
              <p>
                Your personal data is encrypted and stored securely. We only access the minimum 
                required information to provide our services.
              </p>
            </div>

            <div className="privacy-point">
              <h3>📧 Email Privacy</h3>
              <p>
                We only read job-related emails and never store the full content. Email data 
                is processed temporarily and then discarded.
              </p>
            </div>

            <div className="privacy-point">
              <h3>🚫 No Data Selling</h3>
              <p>
                We never sell, rent, or share your personal information with third parties. 
                Your data belongs to you.
              </p>
            </div>

            <div className="privacy-point">
              <h3>🛡️ Secure Authentication</h3>
              <p>
                We use industry-standard OAuth2 authentication with Google, ensuring your 
                account remains secure.
              </p>
            </div>
          </div>
        </section>

        <section className="getting-started-section">
          <h2>Getting Started</h2>
          <div className="getting-started-content">
            <p>
              Ready to streamline your job search? Here's how to get started:
            </p>
            <ol>
              <li>
                <strong>Sign In:</strong> Click the "Log In" button and authenticate with your Google account.
              </li>
              <li>
                <strong>Authorize Gmail:</strong> Grant permission to scan your Gmail for job applications.
              </li>
              <li>
                <strong>Scan Emails:</strong> Use the Gmail scraper to automatically process your recent emails.
              </li>
              <li>
                <strong>Add Manually:</strong> Use the "Add Application" feature for applications not in your email.
              </li>
              <li>
                <strong>Track Progress:</strong> Monitor your applications using the interactive dashboard.
              </li>
            </ol>
          </div>
        </section>

        <section className="support-section">
          <h2>Need Help?</h2>
          <div className="support-content">
            <p>
              If you have any questions or need assistance, please don't hesitate to reach out:
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <strong>Email:</strong> support@jobapptracker.com
              </div>
              <div className="contact-item">
                <strong>Documentation:</strong> Check our setup guides for detailed instructions
              </div>
              <div className="contact-item">
                <strong>Issues:</strong> Report bugs or feature requests through our feedback system
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
